/* global t, safeArray, num, invoiceGross, activePaymentsForDocument, creditNotesForInvoice, activeCustomerSettlements */
// KUNDEFORDRINGER — lesemodell for byråets reskontroskjerm
//
// Avstemming av kundefordringer er den ene kontrollen saldoen alene ikke kan
// gjøre: 1500 kan stemme til øret mot hovedboken og likevel bestå av tolv
// fordringer som aldri blir betalt. Skjermen trenger derfor postene, ikke bare
// tallet — og den trenger dem PER PERIODESLUTT, ikke per i dag.
//
// Det er hele grunnen til at åpent beløp regnes ut her og ikke hentes fra
// `invoicePaymentSummary`: den summeringen svarer på hva som står åpent NÅ. En
// avstemming per 31.08 skal vise fordringen slik den var 31.08, og så si at den
// kom inn 03.09. Uten det skillet forsvinner nettopp den etterfølgende
// innbetalingen som er den beste dokumentasjonen på at fordringen var reell.
//
// Byggeklossene er likevel appens egne — `invoiceGross`,
// `activePaymentsForDocument`, `creditNotesForInvoice`,
// `activeCustomerSettlements`. Modellen finner ikke opp nye regler for hva som
// er betalt; den setter bare en datogrense på de samme postene.
//
// Modellen er READ-ONLY og har ingen mockdata. Har foretaket ingen åpne poster,
// er lista tom og flaten sier det.
//
// Det som IKKE er portert fra designet, fordi appen ikke har kilden:
//   · valutaposter — salgsfakturaen føres i NOK, det finnes ingen kurs å kontrollere
//   · omtvistet — ingen tvistemarkering på fakturaen; den kommer fra byråets
//     egen vurdering og legges på i flaten, ikke her
// Å utlede dem av fritekst ville vært en regel uten kilde.

const RECV_FLAG = {
  gammel: { get label() { return t('rcr.flag.old', 'Gammel post over 90 dager'); }, cls: 'error' },
  forfalt30: { get label() { return t('rcr.flag.overdue30', 'Forfalt over 30 dager'); }, cls: 'warning' },
  negativ: { get label() { return t('rcr.flag.negative', 'Negativ kundesaldo'); }, cls: 'error' },
  duplikat: { get label() { return t('rcr.flag.duplicate', 'Mulig duplikat'); }, cls: 'error' },
  betalt_apen: { get label() { return t('rcr.flag.partlyPaid', 'Delbetalt, fortsatt åpen'); }, cls: 'warning' },
  kreditnota_apen: { get label() { return t('rcr.flag.creditNoteOpen', 'Kreditnota ikke utlignet'); }, cls: 'warning' },
  inkasso: { get label() { return t('rcr.flag.collection', 'Inkassovarsel sendt'); }, cls: 'error' },
  tapsrisiko: { get label() { return t('rcr.flag.lossRisk', 'Tapsrisiko'); }, cls: 'error' },
  mangler_dok: { get label() { return t('rcr.flag.missingDoc', 'Mangler fakturadokument'); }, cls: 'warning' },
  innbetalt_etter: { get label() { return t('rcr.flag.paidAfter', 'Innbetalt etter periodedato'); }, cls: 'success' },
  kreditnota_etter: { get label() { return t('rcr.flag.creditNoteAfter', 'Kreditnota etter periodedato'); }, cls: 'info' },
  // Tvisten finnes ikke i dataene. Den kommer fra byraaets egen vurdering og
  // legges paa i flaten — men den vises som et varsel, fordi det er det den er.
  omtvistet: { get label() { return t('rcr.flag.disputed', 'Omtvistet faktura'); }, cls: 'error' },
};

// Reskontroen ligger paa appens egen kundefordringskonto. En annen konto i
// omraadet har ingen underliggende reskontro, og da er differansen ikke et
// avvik men et fravaer av grunnlag flaten maa kunne si fra om.
const RECV_SUBLEDGER_ACCOUNT = '1500';

const RECV_AGE_BUCKETS = [
  { key: 'uten_forfall', get label() { return t('rcr.age.noDueDate', 'Uten forfallsdato'); } },
  { key: 'ikke_forfalt', get label() { return t('rcr.age.notDue', 'Ikke forfalt'); } },
  { key: 'd1_30', get label() { return t('rcr.age.d1_30', '1–30 dager forfalt'); } },
  { key: 'd31_60', get label() { return t('rcr.age.d31_60', '31–60 dager forfalt'); } },
  { key: 'd61_90', get label() { return t('rcr.age.d61_90', '61–90 dager forfalt'); } },
  { key: 'over90', get label() { return t('rcr.age.over90', 'Over 90 dager forfalt'); } },
];

// Uten kredittgrense på kundekortet finnes ingen grense å måle mot. Da vises
// utnyttelsen ikke som 0 % — den vises ikke i det hele tatt.
const recvAgeBucket = (days) => {
  if (days === null || days === undefined) return 'uten_forfall';
  if (days <= 0) return 'ikke_forfalt';
  if (days <= 30) return 'd1_30';
  if (days <= 60) return 'd31_60';
  if (days <= 90) return 'd61_90';
  return 'over90';
};

const recvRound = (value) => Math.round((num(value) + Number.EPSILON) * 100) / 100;
// Datoer kommer i to former: `dd.mm.yyyy` fra det brukeren skriver, og
// ISO-tidsstempler fra sendehendelsene. Punktumsjekken maa derfor treffe
// DATOFORMATET og ikke bare et punktum — et tidsstempel har punktum i
// millisekundene, og ble ellers snudd til soppel og forsvant ut av kontrollen.
const recvIso = (value) => {
  const text = String(value || '').trim();
  if (!text) return null;
  const iso = /^\d{1,2}\.\d{1,2}\.\d{4}$/.test(text)
    ? text.split('.').reverse().map((part, index) => (index ? part.padStart(2, '0') : part)).join('-')
    : text.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : null;
};
const recvDaysBetween = (fromIso, toIso) => Math.round(
  (new Date(toIso + 'T00:00:00') - new Date(fromIso + 'T00:00:00')) / 86400000,
);

// Dokumentene reskontroen består av: utstedte salgsfakturaer og kreditnotaer.
// Tilbud og ordrebekreftelser er ikke fordringer — de er ikke bokført.
const recvIsLedgerDocument = (invoice) => invoice
  && invoice.status !== 'draft' && invoice.status !== 'annulled'
  && (invoice.kind === 'credit' || !invoice.kind || invoice.kind === 'invoice');

// Innfordringen leses av dokumentets egne sendinger. `purring` er appens
// strengere oppfølgingsdokument, `reminder` den vanlige purringen — begge er
// noe som faktisk er sendt, ikke en status noen har satt.
const recvCollection = (invoice, periodTo) => {
  const events = safeArray(invoice.sendEvents).filter((event) => {
    const at = recvIso(event?.sentAt);
    return !at || at <= periodTo;
  });
  const reminders = events.filter((event) => event.variant === 'reminder').length;
  const notices = events.filter((event) => event.variant === 'purring').length;
  if (notices) return { label: notices === 1 ? t('rcr.collection.noticeOne', 'Inkassovarsel sendt') : t('rcr.collection.noticeMany', '{count} inkassovarsler sendt', { count: notices }), reminders, notices };
  if (reminders) return { label: reminders === 1 ? t('rcr.collection.reminderOne', 'Én purring sendt') : t('rcr.collection.reminderMany', '{count} purringer sendt', { count: reminders }), reminders, notices };
  if (events.length || invoice.sentAt) return { label: t('rcr.collection.invoiceSent', 'Faktura sendt'), reminders, notices };
  return { label: t('rcr.collection.notSent', 'Ikke sendt'), reminders, notices };
};

// Åpent beløp per periodeslutt, og hva som skjedde etterpå. Betalinger og
// kreditnotaer deles på periodedatoen: det som kom før er en del av saldoen,
// det som kom etter er dokumentasjon på den.
const recvOpenAt = (state, invoice, periodTo) => {
  const isCredit = invoice.kind === 'credit';
  const gross = recvRound(invoiceGross(invoice));
  const signed = isCredit ? -Math.abs(gross) : Math.abs(gross);
  const direction = isCredit ? 'outgoing' : 'incoming';
  let settledBefore = 0;
  let settledAfter = 0;
  let settledAfterDate = null;
  for (const payment of activePaymentsForDocument(state, 'invoice', invoice.id)) {
    const paymentSign = (payment.direction || direction) === direction ? 1 : -1;
    const amount = paymentSign * Math.max(0, num(payment.amount));
    if (!amount) continue;
    const date = recvIso(payment.date) || recvIso(payment.createdAt);
    if (date && date > periodTo) {
      settledAfter += amount;
      if (!settledAfterDate || date < settledAfterDate) settledAfterDate = date;
    } else settledBefore += amount;
  }
  // Kreditnotaer som krediterer denne fakturaen, og motregninger av kundens
  // tilgodehavende. Begge reduserer fordringen — men bare hvis de er datert
  // innenfor perioden.
  let creditedBefore = 0;
  let creditedAfter = null;
  if (!isCredit) {
    for (const note of creditNotesForInvoice(state, invoice.id)) {
      const date = recvIso(note.date);
      const amount = Math.abs(recvRound(invoiceGross(note)));
      if (!amount) continue;
      if (date && date > periodTo) {
        creditedAfter = {
          docNumber: note.invoiceNumber || note.id,
          date,
          amount: recvRound((creditedAfter?.amount || 0) + amount),
        };
      } else creditedBefore += amount;
    }
    for (const settlement of activeCustomerSettlements(state)) {
      if (settlement.kind !== 'credit_offset' || settlement.invoiceId !== invoice.id) continue;
      const date = recvIso(settlement.date);
      if (date && date > periodTo) continue;
      creditedBefore += Math.max(0, num(settlement.amount));
    }
  } else {
    // A credit note linked to an invoice is already allocated through that
    // invoice. Keep the credit note itself out of the open-item balance, or
    // the same reduction is counted twice. An unlinked credit note remains a
    // negative customer balance until an explicit offset is recorded.
    const sourceInvoice = safeArray(state.invoices).find((candidate) => (
      candidate?.id === invoice.creditedFromInvoiceId
      && recvIsLedgerDocument(candidate)
    ));
    const sourceDate = recvIso(sourceInvoice?.date);
    if (sourceInvoice && (!sourceDate || sourceDate <= periodTo)) {
      creditedBefore += Math.abs(signed);
    }
    for (const settlement of activeCustomerSettlements(state)) {
      if (settlement.kind !== 'credit_offset' || settlement.creditNoteId !== invoice.id) continue;
      const date = recvIso(settlement.date);
      if (date && date > periodTo) continue;
      creditedBefore += Math.max(0, num(settlement.amount));
    }
  }
  const reduced = settledBefore + creditedBefore;
  // Preserve customer credits and outgoing refunds, including refunds on linked notes.
  const open = isCredit ? signed + reduced : signed - reduced;
  return {
    gross: signed,
    openAmount: recvRound(open),
    settledBefore: recvRound(settledBefore),
    settledAfter: recvRound(settledAfter),
    settledAfterDate,
    creditedBefore: recvRound(creditedBefore),
    creditedAfter,
  };
};

const recvImportedItemKey = (item = {}) => [
  String(item.kind || '').trim(),
  String(item.invoiceNumber || '').trim().toLocaleLowerCase('nb-NO'),
  String(item.partyName || '').trim().toLocaleLowerCase('nb-NO'),
  String(item.date || '').trim(),
].join(':');

const recvImportedOpenAt = (state, item, periodTo) => {
  const gross = recvRound(item.remainingAmount ?? item.outstanding ?? item.amountGross ?? item.amount);
  const settlement = safeArray(state.accountingImportOpenItemSettlements)
    .find((entry) => String(entry?.itemKey || '').trim() === recvImportedItemKey(item));
  const settledAmount = gross > 0 ? Math.min(gross, Math.max(0, num(settlement?.settledAmount))) : 0;
  const settledAt = recvIso(settlement?.settledAt);
  const settledBefore = settledAt && settledAt <= periodTo ? settledAmount : 0;
  const settledAfter = settledAt && settledAt > periodTo ? settledAmount : 0;
  return {
    gross,
    openAmount: recvRound(gross - settledBefore),
    settledBefore: recvRound(settledBefore),
    settledAfter: recvRound(settledAfter),
    settledAfterDate: settledAfter ? settledAt : null,
    creditedBefore: 0,
    creditedAfter: null,
  };
};

// To dokumenter på samme kunde, med samme beløp og samme fakturadato, er et
// duplikat verdt å se på. Det er et forslag til gjennomgang, ikke en
// konklusjon: en kunde kan bestille det samme to ganger samme dag.
const recvDuplicateKey = (item) => [
  String(item.customer || '').trim().toLowerCase(),
  item.date || '',
  Math.abs(item.gross).toFixed(2),
].join('|');

const recvBuildItems = (state, periodTo, today) => {
  const rows = [];
  for (const invoice of safeArray(state.invoices)) {
    if (!recvIsLedgerDocument(invoice)) continue;
    const issued = recvIso(invoice.date);
    // Et dokument utstedt etter periodeslutt er ikke en del av denne saldoen.
    if (issued && issued > periodTo) continue;
    const balance = recvOpenAt(state, invoice, periodTo);
    if (Math.abs(balance.openAmount) < 0.005) continue;
    const due = recvIso(invoice.dueDate);
    const daysOverdue = due && balance.openAmount > 0.005 ? recvDaysBetween(due, periodTo) : null;
    rows.push({
      id: invoice.id,
      isCredit: invoice.kind === 'credit',
      typeLabel: invoice.kind === 'credit' ? t('rcr.type.creditNote', 'Kreditnota') : t('rcr.type.invoice', 'Faktura'),
      docNumber: invoice.invoiceNumber || invoice.id,
      customer: invoice.customer?.name || invoice.customerName || t('sub.unknownCustomer', 'Ukjent kunde'),
      customerId: invoice.customer?.id || invoice.customerId || '',
      organizationNumber: invoice.customer?.organizationNumber || invoice.customerOrganizationNumber || '',
      date: issued,
      dueDate: due,
      daysOverdue,
      ageBucket: recvAgeBucket(daysOverdue),
      gross: balance.gross,
      openAmount: balance.openAmount,
      settledBefore: balance.settledBefore,
      settledAfter: balance.settledAfter,
      settledAfterDate: balance.settledAfterDate,
      creditedAfter: balance.creditedAfter,
      // Reskontroen ligger paa appens egen kundefordringskonto. Kontrollkontoen
      // staar paa posten slik at flaten kan filtrere paa den, ogsaa naar
      // kontoplanen har flere kontoer i omraadet.
      ledgerCode: RECV_SUBLEDGER_ACCOUNT,
      collection: recvCollection(invoice, periodTo),
      // Fakturadokumentet er bilaget. Mangler det, er posten uten
      // dokumentasjon — samme spørsmål bankområdet stiller om vedlegg.
      hasDocument: Boolean(invoice.pdfStoragePath || invoice.pdf?.storagePath || invoice.pdfDownloadUrl),
      flags: [],
      duplicateOf: [],
      subsequentTo: today,
    });
  }

  for (const item of safeArray(state.accountingImportOpenItems?.customerInvoices)) {
    const issued = recvIso(item.date);
    if (issued && issued > periodTo) continue;
    const balance = recvImportedOpenAt(state, item, periodTo);
    if (Math.abs(balance.openAmount) < 0.005) continue;
    const due = recvIso(item.dueDate);
    const daysOverdue = due ? recvDaysBetween(due, periodTo) : null;
    const importedRow = {
      id: item.id || recvImportedItemKey(item),
      isCredit: balance.openAmount < 0,
      typeLabel: balance.openAmount < 0 ? t('rcr.type.importedCredit', 'Importert kundekredit') : t('rcr.type.importedInvoice', 'Importert faktura'),
      docNumber: item.invoiceNumber || item.id || t('rcr.fallback.importedItem', 'Importert post'),
      customer: item.partyName || t('sub.unknownCustomer', 'Ukjent kunde'),
      customerId: item.partyId || '',
      organizationNumber: item.organizationNumber || '',
      date: issued,
      dueDate: due,
      daysOverdue,
      ageBucket: recvAgeBucket(daysOverdue),
      gross: balance.gross,
      openAmount: balance.openAmount,
      settledBefore: balance.settledBefore,
      settledAfter: balance.settledAfter,
      settledAfterDate: balance.settledAfterDate,
      creditedBefore: 0,
      creditedAfter: null,
      ledgerCode: RECV_SUBLEDGER_ACCOUNT,
      collection: { label: t('rcr.collection.imported', 'Importert åpen post'), reminders: 0, notices: 0 },
      hasDocument: Boolean(item.importMeta?.sourcePath || item.importSource),
      imported: true,
      flags: [],
      duplicateOf: [],
      subsequentTo: today,
    };
    const duplicateLiveRow = rows.some((row) => recvDuplicateKey(row) === recvDuplicateKey(importedRow)
      && String(row.docNumber || '').trim() === String(importedRow.docNumber || '').trim());
    if (!duplicateLiveRow) rows.push(importedRow);
  }

  const byDuplicateKey = new Map();
  for (const row of rows) {
    const key = recvDuplicateKey(row);
    if (!byDuplicateKey.has(key)) byDuplicateKey.set(key, []);
    byDuplicateKey.get(key).push(row);
  }
  for (const group of byDuplicateKey.values()) {
    if (group.length < 2) continue;
    for (const row of group) {
      row.duplicateOf = group.filter((other) => other.id !== row.id).map((other) => other.docNumber);
    }
  }

  for (const row of rows) {
    const days = row.daysOverdue;
    if (days !== null && days > 90) row.flags.push('gammel');
    else if (days !== null && days > 30) row.flags.push('forfalt30');
    if (row.openAmount < -0.005) row.flags.push('negativ');
    if (row.duplicateOf.length) row.flags.push('duplikat');
    if (!row.isCredit && row.settledBefore > 0.005) row.flags.push('betalt_apen');
    if (row.isCredit) row.flags.push('kreditnota_apen');
    if (row.collection.notices > 0) row.flags.push('inkasso');
    // Tapsrisiko er ikke en konklusjon her — det er spørsmålet flaten stiller.
    // Den stilles der fordringen enten er eldre enn 90 dager eller er eskalert
    // til inkassovarsel. Nedskrivning og tapsføring avgjør regnskapsføreren.
    if ((days !== null && days > 90) || row.collection.notices > 0) row.flags.push('tapsrisiko');
    if (!row.hasDocument) row.flags.push('mangler_dok');
    if (row.settledAfter > 0.005) row.flags.push('innbetalt_etter');
    if (row.creditedAfter) row.flags.push('kreditnota_etter');
  }
  return rows.sort((left, right) => (right.daysOverdue ?? -9999) - (left.daysOverdue ?? -9999));
};


Object.assign(window, {
  RECV_FLAG, RECV_AGE_BUCKETS, RECV_SUBLEDGER_ACCOUNT,
  recvAgeBucket, recvOpenAt, recvCollection, recvBuildItems,
  recvRound, recvIso, recvDuplicateKey,
});
